import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { albumRequestId, formatFileSize } from "@/lib/albumUploads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { t } from "@/lib/i18n";

const PHOTO = /\.(jpe?g|png|gif|webp)$/i;
const AUDIO = /\.(mp3|wav|ogg|m4a|aac|flac)$/i;
const VIDEO = /\.(mp4|mov|webm)$/i;

export function mediaQueue(files, folder = false) {
  return Array.from(files).map((file) => {
    const kind = PHOTO.test(file.name) ? "photo" : VIDEO.test(file.name) ? "video" : AUDIO.test(file.name) ? "audio" : null;
    return { id: albumRequestId(), file, kind, selected: !!kind, status: kind ? "pending" : "skipped", error: kind ? "" : "Unsupported media file." };
  });
}

// Both screens use the same upload, destination selection and durable retry flow.
export default function MemberMediaUpload({ memberId, folder = false, initialFiles, onChanged, onBusy, onComplete }) {
  const [queue, setQueue] = useState(() => mediaQueue(initialFiles || [], folder));
  const [review, setReview] = useState(null), [destination, setDestination] = useState("");
  const [busy, setBusy] = useState(false), [errors, setErrors] = useState([]);
  const batch = useRef(albumRequestId()), abort = useRef(null), mounted = useRef(true);
  const manifest = useRef(null);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; abort.current?.abort(); }; }, []);
  const pending = queue.filter((q) => q.selected && !["uploaded", "skipped"].includes(q.status));
  const resetReview = () => { batch.current = albumRequestId(); manifest.current = null; setReview(null); setDestination(""); };
  const update = (id, fields) => { if (mounted.current) setQueue((q) => q.map((item) => item.id === id ? { ...item, ...fields } : item)); };
  const send = async (commit = false) => {
    const files = manifest.current || pending.map((q) => ({ id: q.id, name: q.file.name, size: q.file.size, kind: q.kind }));
    const { data } = await api.post(`/persons/${memberId}/media/batches`, {
      batch_id: batch.current, files,
      destination_id: destination || null, commit,
    });
    if (data.committed) manifest.current = files;
    setReview(data); return data;
  };
  const run = async (upload) => {
    let completed = false;
    setBusy(true); onBusy?.(true); setErrors([]);
    try {
      const data = await send(upload);
      if (!upload || !data.committed) {
        if (data.error) setErrors([data.error]);
        return;
      }
      abort.current = new AbortController();
      const failures = [];
      for (let index = 0; index < pending.length; index++) {
        if (abort.current.signal.aborted) break;
        const entry = pending[index];
        const route = data.routes.find((r) => r.id === entry.id);
        if (route?.destination === "rejected") {
          update(entry.id, { status: "failed", error: route.error }); failures.push(`${entry.file.name}: ${route.error}`); continue;
        }
        update(entry.id, { status: "uploading", error: "", progress: 0 });
        const body = new FormData(); body.append("file", entry.file);
        try {
          const response = await api.post(`/persons/${memberId}/media/uploads`, body, {
            headers: { "X-Batch-Id": batch.current, "X-Upload-Id": entry.id }, signal: abort.current.signal,
            onUploadProgress: ({ loaded, total }) => update(entry.id, { progress: total ? Math.round(loaded / total * 100) : 0 }),
          });
          update(entry.id, { status: "uploaded", progress: 100, destination: response.data.destination });
          await onChanged?.(response.data);
        } catch (error) {
          const message = formatApiErrorDetail(error.response?.data?.detail) || "Upload interrupted. Retry this file.";
          update(entry.id, { status: abort.current.signal.aborted ? "cancelled" : "failed", error: message });
          if (!abort.current.signal.aborted) failures.push(`${entry.file.name}: ${message}`);
        }
        if (index < pending.length - 1 && !abort.current.signal.aborted) {
          await new Promise((resolve) => {
            const signal = abort.current.signal;
            const done = () => { clearTimeout(timer); signal.removeEventListener("abort", done); resolve(); };
            const timer = setTimeout(done, 3200); signal.addEventListener("abort", done, { once: true });
          });
        }
      }
      if (failures.length && mounted.current) setErrors(failures);
      completed = pending.length > 0 && !failures.length && !abort.current.signal.aborted;
    } catch (error) { if (mounted.current) setErrors([formatApiErrorDetail(error.response?.data?.detail) || "Upload failed"]); }
    finally { abort.current = null; if (mounted.current) setBusy(false); onBusy?.(false); }
    if (completed && mounted.current) onComplete?.();
  };
  return <div className="space-y-3">
    {!initialFiles && <Input aria-label={t(folder ? "Choose folder" : "Choose audio, photos and videos")} type="file" multiple
      accept=".jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.webm,.mp3,.wav,.ogg,.m4a,.aac,.flac"
      {...(folder ? { webkitdirectory: "", directory: "" } : {})} disabled={busy}
      onChange={(event) => { resetReview(); setQueue(mediaQueue(event.target.files, folder)); event.target.value = ""; }} />}
    <p className="text-sm">{t("Audio, photos and videos share the account storage allowance. Eligible excess photos and videos use the linked member's subscription.")}</p>
    <ul className="max-h-64 overflow-auto space-y-2">
      {queue.map((q) => <li key={q.id} className="rounded border p-2 text-sm">
        <label className="flex items-center gap-2"><input type="checkbox" checked={q.selected} disabled={busy || ["uploaded", "skipped"].includes(q.status)}
          onChange={(e) => { resetReview(); setQueue((items) => items.map((item) => item.id === q.id ? { ...item, selected: e.target.checked } : item)); }} />
          <span className="break-all">{q.file.webkitRelativePath || q.file.name}</span><span className="shrink-0">{formatFileSize(q.file.size)}</span></label>
        <p role="status">{t(q.status)} {q.status === "uploading" ? `${q.progress || 0}%` : ""} {q.error && t(q.error)}</p>
      </li>)}
    </ul>
    {review && (review.requires_destination || review.error) && <div className="rounded border bg-amber-50 p-3 space-y-2" role="region" aria-label={t("Upload destinations")}>
      <p>{t("Default limits")}: {review.limits.max_photos} {t("photos")} × {review.limits.max_photo_mb} MB; {review.limits.max_videos} {t("videos")} × {review.limits.max_video_mb} MB.</p>
      <ul>{review.routes.map((r) => <li key={r.id} className="break-all">{r.name}: {t(r.destination === "paid" ? "Additional album" : r.destination === "default" ? "Default album" : "rejected")}{r.error && ` — ${t(r.error)}`}</li>)}</ul>
      {review.error && <p role="alert" className="text-red-800">{t(review.error)}</p>}
      {review.requires_destination && <><p>{t("Album creation limit reached. Select an existing eligible album for the overflow photos.")}</p>
        <select aria-label={t("Existing album for overflow")} className="w-full rounded border p-2" value={destination} disabled={busy} onChange={(e) => setDestination(e.target.value)}>
          <option value="">{t("Select an album")}</option>
          {review.destinations.map((d) => <option key={d.id} value={d.id} disabled={!d.eligible}>{d.title}{d.reason ? ` — ${t(d.reason)}` : ""}</option>)}
        </select></>}
      {review.automatic && <p>{t("One additional album will be created for this batch. Retries reuse it.")}</p>}
    </div>}
    <div className="flex flex-wrap gap-2">
      <Button disabled={busy || !pending.length || !!review?.error || (!!review?.requires_destination && !destination)} onClick={() => run(true)}>{t("Upload")}</Button>
      {busy && <Button variant="outline" onClick={() => abort.current?.abort()}>{t("Cancel uploads")}</Button>}
    </div>
    <AlertDialog open={errors.length > 0} onOpenChange={(open) => { if (!open) setErrors([]); }}>
      <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{t("Unable to add photos")}</AlertDialogTitle>
        <AlertDialogDescription>{t("Review the errors below. Your selected files are kept so you can adjust the selection or retry.")}</AlertDialogDescription></AlertDialogHeader>
        <ul className="max-h-64 overflow-auto text-sm text-red-800">{errors.map((error, i) => <li key={i}>{t(error)}</li>)}</ul>
        <AlertDialogFooter><AlertDialogCancel>{t("Close")}</AlertDialogCancel></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </div>;
}
